/**
 * Formularios de alta y edicion, en una ventana modal.
 *
 * Dos ideas que guian esto:
 *
 *  - **Avisar sin bloquear.** Si un apellido no cuadra con el del padre, o
 *    una fecha es imposible, se dice en el momento y en claro, pero el boton
 *    de guardar sigue activo. A veces la realidad de una familia es irregular
 *    y a veces es un error de dedo, y quien decide no es el programa.
 *
 *  - **Nada toca el modelo a mano.** Todo pasa por src/core/editar.ts, que es
 *    el unico sitio donde se escriben las tres relaciones redundantes del
 *    formato, siempre a la vez.
 */
import {
  anadirHijo,
  asignarProgenitores,
  borrarFamilia,
  crearPersona,
  datosDe,
  editarPersona,
  familiaDePareja,
  ponerTipoUnion,
  quitarHijo,
  DATOS_VACIOS,
  type DatosPersona,
} from "../core/editar.js";
import type { Indice } from "../core/grafo.js";
import {
  apellidoPrimario,
  nombreCompleto,
  nombrePrincipal,
  type Arbol,
  type Sexo,
  type TipoUnion,
} from "../core/model.js";
import { buscar, campo, el, entrada, selectDe, vaciar, type OpcionSelect } from "./dom.js";

const RE_FECHA = /^(\d{4})(-\d{2}-\d{2})?$/;

const SEXOS: OpcionSelect[] = [
  { valor: "M", texto: "hombre (M)" },
  { valor: "F", texto: "mujer (F)" },
  { valor: "U", texto: "sin registrar (U)" },
];

const DERIVACIONES: OpcionSelect[] = [
  { valor: "Patrilineal", texto: "del padre (Patrilineal)" },
  { valor: "Matrilineal", texto: "de la madre (Matrilineal)" },
  { valor: "Inherited", texto: "heredado (Inherited)" },
  { valor: "Taken", texto: "tomado (Taken)" },
  { valor: "", texto: "sin registrar" },
];

const UNIONES: OpcionSelect[] = [
  { valor: "Married", texto: "casados (Married)" },
  { valor: "Unmarried", texto: "pareja sin matrimonio (Unmarried)" },
  { valor: "Unknown", texto: "sin registrar (Unknown)" },
];

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------

export class Modal {
  private readonly caja = buscar<HTMLElement>("#modal");
  private readonly titulo = buscar<HTMLElement>("#modal-titulo");
  private readonly cuerpo = buscar<HTMLElement>("#modal-cuerpo");

  constructor() {
    buscar<HTMLElement>("#modal-cerrar").addEventListener("click", () => this.cerrar());
    this.caja.addEventListener("click", (ev) => {
      if (ev.target === this.caja) this.cerrar();
    });
    window.addEventListener("keydown", (ev) => {
      if (ev.key === "Escape" && !this.caja.classList.contains("oculto")) this.cerrar();
    });
  }

  abrir(titulo: string, contenido: HTMLElement): void {
    this.titulo.textContent = titulo;
    vaciar(this.cuerpo);
    this.cuerpo.appendChild(contenido);
    this.caja.classList.remove("oculto");
  }

  cerrar(): void {
    this.caja.classList.add("oculto");
    vaciar(this.cuerpo);
  }
}

function opcionesPersonas(
  arbol: Arbol,
  excluir: string[] = [],
  vacio = "— nadie —",
): OpcionSelect[] {
  const ls = [...arbol.personas.entries()]
    .filter(([h]) => !excluir.includes(h))
    .map(([h, p]) => ({ valor: h, texto: `${nombreCompleto(p)} · ${p.id ?? ""}` }))
    .sort((a, b) => a.texto.localeCompare(b.texto, "es"));
  return [{ valor: "", texto: vacio }, ...ls];
}

// ---------------------------------------------------------------------------
// Formulario de persona
// ---------------------------------------------------------------------------

export interface OpcionesFormularioPersona {
  arbol: Arbol;
  ix: Indice;
  /** undefined = persona nueva. */
  handle?: string;
  modal: Modal;
  alGuardar(handle: string): void;
}

export function formularioPersona(op: OpcionesFormularioPersona): void {
  const { arbol, ix, handle, modal } = op;
  const d: DatosPersona = handle ? datosDe(arbol, handle) : { ...DATOS_VACIOS };

  const nombres = entrada(d.nombres, "Aurelio Damián");
  const ap1 = entrada(d.apellido1, "Zarco");
  const ap2 = entrada(d.apellido2, "Ibarrola");
  const der1 = selectDe(DERIVACIONES, d.derivacion1);
  const der2 = selectDe(DERIVACIONES, d.derivacion2);
  const sexo = selectDe(SEXOS, d.sexo);
  const nac = entrada(d.nacimiento, "2001-08-13 ó 2001");
  const lugarNac = entrada(d.lugarNacimiento, "Valdeluz");
  const mue = entrada(d.fallecimiento, "1998-11-02 ó 1998");
  const lugarMue = entrada(d.lugarFallecimiento, "");

  const avisos = el("div", { clase: "avisos-formulario" });

  const leer = (): DatosPersona => ({
    nombres: nombres.value,
    apellido1: ap1.value,
    apellido2: ap2.value,
    derivacion1: der1.value,
    derivacion2: der2.value,
    sexo: sexo.value as Sexo,
    nacimiento: nac.value,
    lugarNacimiento: lugarNac.value,
    fallecimiento: mue.value,
    lugarFallecimiento: lugarMue.value,
  });

  /**
   * Avisos en vivo. Se comprueban las cosas que se pueden saber sin guardar:
   * el formato de las fechas y si los apellidos cuadran con los progenitores
   * que ya estan registrados.
   */
  const revisar = (): void => {
    vaciar(avisos);
    const v = leer();
    const msg = (texto: string, clase = "aviso"): void => {
      avisos.appendChild(el("div", { clase: `incidencia ${clase}`, texto }));
    };

    if (!v.nombres.trim() && !v.apellido1.trim()) {
      msg("No hay ni nombre de pila ni apellido. Al menos uno hace falta para poder identificarla.", "error");
    }
    for (const [etiqueta, valor] of [
      ["nacimiento", v.nacimiento],
      ["fallecimiento", v.fallecimiento],
    ] as const) {
      const t = (valor ?? "").trim();
      if (t && !RE_FECHA.test(t)) {
        msg(`La fecha de ${etiqueta} debe ser AAAA-MM-DD o solo AAAA. "${t}" no lo es.`, "error");
      }
    }
    const a = Number(RE_FECHA.exec((v.nacimiento ?? "").trim())?.[1]);
    const b = Number(RE_FECHA.exec((v.fallecimiento ?? "").trim())?.[1]);
    if (Number.isFinite(a) && Number.isFinite(b) && b < a) {
      msg(`Consta el fallecimiento en ${b}, antes del nacimiento en ${a}.`);
    }

    if (handle) {
      for (const g of ix.progenitores(handle)) {
        if (g.padre) {
          const pa = arbol.personas.get(g.padre);
          const suyo = apellidoPrimario(nombrePrincipal(pa as never))?.texto.trim();
          if (suyo && v.apellido1.trim() && suyo !== v.apellido1.trim()) {
            msg(
              `El primer apellido no coincide con el del padre ${pa ? nombreCompleto(pa) : ""}: "${suyo}". Puede ser correcto, decide tú.`,
            );
          }
        }
        if (g.madre) {
          const ma = arbol.personas.get(g.madre);
          const suyo = apellidoPrimario(nombrePrincipal(ma as never))?.texto.trim();
          if (suyo && v.apellido2.trim() && suyo !== v.apellido2.trim()) {
            msg(
              `El segundo apellido no coincide con el primero de la madre ${ma ? nombreCompleto(ma) : ""}: "${suyo}". Puede ser correcto, decide tú.`,
            );
          }
        }
      }
    }
  };

  for (const c of [nombres, ap1, ap2, nac, mue]) c.addEventListener("input", revisar);
  for (const c of [der1, der2, sexo]) c.addEventListener("change", revisar);

  const campos = el("div", {
    clase: "campos",
    hijos: [
      campo("Nombres de pila", nombres, "Puede quedar vacío: hay personas conocidas solo por el apellido."),
      el("fieldset", {
        hijos: [
          el("legend", { texto: "Apellidos" }),
          el("div", {
            clase: "campos",
            hijos: [
              el("div", {
                clase: "fila-campos",
                hijos: [campo("Primer apellido", ap1), campo("Origen", der1)],
              }),
              el("div", {
                clase: "fila-campos",
                hijos: [campo("Segundo apellido", ap2), campo("Origen", der2)],
              }),
              el("div", {
                clase: "pista",
                texto:
                  "Los dos apellidos se guardan por separado con su origen, no " +
                  "juntos en un campo: así no se pierde qué rama aporta cada uno. " +
                  "Dejar el segundo vacío es válido.",
              }),
            ],
          }),
        ],
      }),
      campo("Sexo", sexo),
      el("fieldset", {
        hijos: [
          el("legend", { texto: "Nacimiento" }),
          el("div", {
            clase: "fila-campos",
            hijos: [campo("Fecha", nac, "AAAA-MM-DD o solo AAAA"), campo("Lugar", lugarNac)],
          }),
        ],
      }),
      el("fieldset", {
        hijos: [
          el("legend", { texto: "Fallecimiento" }),
          el("div", {
            clase: "fila-campos",
            hijos: [campo("Fecha", mue, "Vacío si está viva o no se sabe"), campo("Lugar", lugarMue)],
          }),
        ],
      }),
      avisos,
    ],
  });

  const guardar = el("button", { clase: "primario", texto: handle ? "Guardar" : "Crear" });
  guardar.addEventListener("click", () => {
    const v = leer();
    if (!v.nombres.trim() && !v.apellido1.trim()) {
      revisar();
      return;
    }
    const h = handle ? (editarPersona(arbol, handle, v), handle) : crearPersona(arbol, v);
    modal.cerrar();
    op.alGuardar(h);
  });
  const cancelar = el("button", { texto: "Cancelar" });
  cancelar.addEventListener("click", () => modal.cerrar());

  campos.appendChild(
    el("div", { clase: "pie-modal", hijos: [cancelar, guardar] }),
  );

  revisar();
  modal.abrir(handle ? "Editar persona" : "Nueva persona", campos);
}

// ---------------------------------------------------------------------------
// Formulario de relaciones
// ---------------------------------------------------------------------------

export interface OpcionesFormularioRelaciones {
  arbol: Arbol;
  ix: Indice;
  handle: string;
  modal: Modal;
  alGuardar(): void;
}

export function formularioRelaciones(op: OpcionesFormularioRelaciones): void {
  const { arbol, ix, handle, modal } = op;
  const yo = arbol.personas.get(handle);
  if (!yo) return;

  const raiz = el("div", { clase: "campos" });

  const repintar = (): void => {
    vaciar(raiz);
    const nombreYo = nombreCompleto(yo);

    // --- progenitores ----------------------------------------------------
    const g = ix.progenitores(handle)[0];
    const selPadre = selectDe(opcionesPersonas(arbol, [handle]), g?.padre ?? "");
    const selMadre = selectDe(opcionesPersonas(arbol, [handle]), g?.madre ?? "");
    const familiaActual = g ? arbol.familias.get(g.familia) : undefined;
    const selUnionPadres = selectDe(UNIONES, familiaActual?.rel ?? "Unknown");

    const aplicarProgenitores = el("button", { texto: "Aplicar progenitores" });
    aplicarProgenitores.addEventListener("click", () => {
      const padre = selPadre.value || undefined;
      const madre = selMadre.value || undefined;
      if (!padre && !madre) {
        // Quitarle los progenitores: se saca de su familia como hijo.
        for (const fh of [...(arbol.personas.get(handle)?.childof ?? [])]) {
          quitarHijo(arbol, fh, handle);
        }
      } else {
        asignarProgenitores(arbol, handle, padre, madre, selUnionPadres.value as TipoUnion);
      }
      op.alGuardar();
      repintar();
    });

    raiz.appendChild(
      el("fieldset", {
        hijos: [
          el("legend", { texto: `De quién es hija ${nombreYo}` }),
          el("div", {
            clase: "campos",
            hijos: [
              el("div", {
                clase: "fila-campos",
                hijos: [campo("Padre", selPadre), campo("Madre", selMadre)],
              }),
              campo("Tipo de unión de los progenitores", selUnionPadres),
              el("div", {
                clase: "pista",
                texto:
                  "Dejar uno de los dos vacío es válido: el árbol real tiene " +
                  "nueve familias con un solo progenitor registrado. Dejar los " +
                  "dos vacíos le quita los progenitores.",
              }),
              el("div", { clase: "acciones", hijos: [aplicarProgenitores] }),
            ],
          }),
        ],
      }),
    );

    // --- parejas ---------------------------------------------------------
    const listaParejas = el("ul", { clase: "lista" });
    for (const v of ix.parejas(handle)) {
      const otra = arbol.personas.get(v.otra);
      const fila = el("li");
      const selRel = selectDe(UNIONES, v.rel, (nuevo) => {
        ponerTipoUnion(arbol, v.familia, nuevo as TipoUnion);
        op.alGuardar();
      });
      const quitar = el("button", { clase: "peligro", texto: "Quitar" });
      quitar.addEventListener("click", () => {
        const f = arbol.familias.get(v.familia);
        // Si la familia tiene hijos no se borra: se quita solo a esta persona
        // del rol que ocupa, para no perder la filiacion de los hijos.
        if (f && f.hijos.length) {
          if (f.padre === handle) f.padre = undefined;
          if (f.madre === handle) f.madre = undefined;
          const p = arbol.personas.get(handle);
          if (p) p.parentin = p.parentin.filter((x) => x !== v.familia);
        } else {
          borrarFamilia(arbol, v.familia);
        }
        op.alGuardar();
        repintar();
      });
      fila.appendChild(
        el("div", {
          clase: "fila-campos tres",
          hijos: [
            el("div", { texto: otra ? nombreCompleto(otra) : v.otra }),
            selRel,
            quitar,
          ],
        }),
      );
      listaParejas.appendChild(fila);
    }

    const selNuevaPareja = selectDe(
      opcionesPersonas(arbol, [handle, ...ix.parejas(handle).map((v) => v.otra)], "— elegir —"),
      "",
    );
    const selNuevaUnion = selectDe(UNIONES, "Married");
    const anadirPareja = el("button", { texto: "Añadir pareja" });
    anadirPareja.addEventListener("click", () => {
      const otra = selNuevaPareja.value;
      if (!otra) return;
      // Quien va en el rol de padre y quien en el de madre se decide por el
      // sexo; si no se sabe, el que abre el formulario va como padre. El
      // validador avisa si el rol no cuadra con el sexo.
      const otraP = arbol.personas.get(otra);
      let padre = handle;
      let madre = otra;
      if (yo.sexo === "F" || otraP?.sexo === "M") {
        padre = otra;
        madre = handle;
      }
      familiaDePareja(arbol, padre, madre, selNuevaUnion.value as TipoUnion);
      op.alGuardar();
      repintar();
    });

    raiz.appendChild(
      el("fieldset", {
        hijos: [
          el("legend", { texto: "Parejas" }),
          el("div", {
            clase: "campos",
            hijos: [
              listaParejas,
              el("div", {
                clase: "fila-campos tres",
                hijos: [selNuevaPareja, selNuevaUnion, anadirPareja],
              }),
              el("div", {
                clase: "pista",
                texto:
                  "Se pueden registrar varias uniones para la misma persona: " +
                  "es así como aparecen los medios hermanos.",
              }),
            ],
          }),
        ],
      }),
    );

    // --- hijos -----------------------------------------------------------
    const listaHijos = el("ul", { clase: "lista" });
    for (const fh of ix.familiasComoProgenitor(handle)) {
      const f = arbol.familias.get(fh);
      if (!f) continue;
      const otro = f.padre === handle ? f.madre : f.padre;
      const otroP = otro ? arbol.personas.get(otro) : undefined;
      listaHijos.appendChild(
        el("li", {
          hijos: [
            el("div", {
              clase: "pista",
              texto: `${f.id ?? fh} · con ${otroP ? nombreCompleto(otroP) : "progenitor sin registrar"}`,
            }),
          ],
        }),
      );
      for (const r of f.hijos) {
        const c = arbol.personas.get(r.hlink);
        const quitar = el("button", { clase: "peligro", texto: "Quitar" });
        quitar.addEventListener("click", () => {
          quitarHijo(arbol, fh, r.hlink);
          op.alGuardar();
          repintar();
        });
        listaHijos.appendChild(
          el("li", {
            hijos: [
              el("div", {
                clase: "fila-campos",
                hijos: [el("div", { texto: c ? nombreCompleto(c) : r.hlink }), quitar],
              }),
            ],
          }),
        );
      }
      const selHijo = selectDe(
        opcionesPersonas(
          arbol,
          [handle, ...(f.hijos.map((x) => x.hlink) ?? []), ...(otro ? [otro] : [])],
          "— elegir —",
        ),
        "",
      );
      const anadir = el("button", { texto: "Añadir hijo a esta familia" });
      anadir.addEventListener("click", () => {
        if (!selHijo.value) return;
        // Se usa asignarProgenitores para que, si ya era hijo de otra
        // familia, no acabe registrado como hijo en dos sitios.
        asignarProgenitores(
          arbol,
          selHijo.value,
          f.padre,
          f.madre,
          (f.rel ?? "Unknown") as TipoUnion,
        );
        op.alGuardar();
        repintar();
      });
      listaHijos.appendChild(
        el("li", { hijos: [el("div", { clase: "fila-campos", hijos: [selHijo, anadir] })] }),
      );
    }

    if (!ix.familiasComoProgenitor(handle).length) {
      const selHijoSuelto = selectDe(opcionesPersonas(arbol, [handle], "— elegir —"), "");
      const anadir = el("button", { texto: "Añadir hijo" });
      anadir.addEventListener("click", () => {
        if (!selHijoSuelto.value) return;
        const padre = yo.sexo === "F" ? undefined : handle;
        const madre = yo.sexo === "F" ? handle : undefined;
        const f = familiaDePareja(arbol, padre, madre, "Unknown");
        anadirHijo(arbol, f, selHijoSuelto.value);
        op.alGuardar();
        repintar();
      });
      listaHijos.appendChild(
        el("li", {
          hijos: [el("div", { clase: "fila-campos", hijos: [selHijoSuelto, anadir] })],
        }),
      );
    }

    raiz.appendChild(
      el("fieldset", {
        hijos: [
          el("legend", { texto: "Hijos" }),
          el("div", { clase: "campos", hijos: [listaHijos] }),
        ],
      }),
    );

    const cerrar = el("button", { clase: "primario", texto: "Hecho" });
    cerrar.addEventListener("click", () => modal.cerrar());
    raiz.appendChild(el("div", { clase: "pie-modal", hijos: [cerrar] }));
  };

  repintar();
  modal.abrir(`Relaciones de ${nombreCompleto(yo)}`, raiz);
}
